import apiClient from './api'
import { withListDefaults } from '@/lib/listFetch'
import type {
  ApiResponse,
  ApiList,
  Quote,
  QuoteDetail,
  QuoteDocument,
  CreateQuoteRequest,
  EditQuoteRequest,
  QuoteListQuery,
  QuoteSummary,
  QuoteHistory,
  ConvertToContractRequest,
  Invoice,
  ContractDetail,
  SendDocumentRequest,
  PdfLink,
  ExportLink,
  ListQuery,
  CreateOptionalFeeTemplateRequest,
  OptionalFeeTemplate,
  OptionalFeeImageUploadResponse,
  ServiceItem,
  ServiceItemRequest,
} from '@/types'

// 1:1 with Schema_Specification Section 4.5 (quotes / 2.5.x).
export const quotesApi = {
  list: (q: QuoteListQuery = {}) =>
    apiClient
      .get<ApiList<Quote>>('/quotes', { params: withListDefaults(q) })
      .then(r => r.data),

  // Tenant-wide counts for the summary cards. List filters are applied by the
  // list endpoint when a KPI card is clicked.
  summary: () =>
    apiClient
      .get<ApiResponse<QuoteSummary>>('/quotes/summary')
      .then(r => r.data),

  get: (id: string) =>
    apiClient.get<ApiResponse<QuoteDetail>>(`/quotes/${id}`).then(r => r.data),

  history: (id: string, q: ListQuery = {}) =>
    apiClient
      .get<ApiList<QuoteHistory>>(`/quotes/${id}/history`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  create: (body: CreateQuoteRequest) =>
    apiClient.post<ApiResponse<Quote>>('/quotes', body).then(r => r.data),

  update: (id: string, body: EditQuoteRequest) =>
    apiClient.put<ApiResponse<Quote>>(`/quotes/${id}`, body).then(r => r.data),

  remove: (id: string) =>
    apiClient.delete<ApiResponse<null>>(`/quotes/${id}`).then(r => r.data),

  send: (id: string, body: SendDocumentRequest) =>
    apiClient
      .post<ApiResponse<Quote>>(`/quotes/${id}/send`, body)
      .then(r => r.data),

  pdf: (id: string) =>
    apiClient.get<ApiResponse<PdfLink>>(`/quotes/${id}/pdf`).then(r => r.data),

  duplicate: (id: string) =>
    apiClient
      .post<ApiResponse<Quote>>(`/quotes/${id}/duplicate`)
      .then(r => r.data),

  convertToContract: (id: string, body?: ConvertToContractRequest) =>
    apiClient
      .post<
        ApiResponse<ContractDetail>
      >(`/quotes/${id}/convert-to-contract`, body)
      .then(r => r.data),

  convertToInvoice: (id: string) =>
    apiClient
      .post<ApiResponse<Invoice>>(`/quotes/${id}/convert-to-invoice`)
      .then(r => r.data),

  createServiceItem: (body: ServiceItemRequest) =>
    apiClient
      .post<ApiResponse<ServiceItem>>(
        '/quotes/service-items',
        body.image
          ? (() => {
              const form = new FormData()
              Object.entries(body).forEach(([key, value]) => {
                if (key === 'image') return
                if (value === undefined || value === null) return
                form.append(
                  key,
                  typeof value === 'boolean'
                    ? value
                      ? '1'
                      : '0'
                    : (value as string | Blob)
                )
              })
              form.append('image', body.image)
              return form
            })()
          : body,
        body.image
          ? { headers: { 'Content-Type': 'multipart/form-data' } }
          : undefined
      )
      .then(r => r.data),

  listOptionalFeeTemplates: () =>
    apiClient
      .get<ApiResponse<OptionalFeeTemplate[]>>('/quotes/optional-fee-templates')
      .then(r => r.data),

  createOptionalFeeTemplate: (body: CreateOptionalFeeTemplateRequest) =>
    apiClient
      .post<
        ApiResponse<OptionalFeeTemplate>
      >('/quotes/optional-fee-templates', body)
      .then(r => r.data),

  uploadOptionalFeeImage: (image: File) => {
    const form = new FormData()
    form.append('image', image)

    return apiClient
      .post<ApiResponse<OptionalFeeImageUploadResponse>>(
        '/quotes/optional-fee-images',
        form,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      )
      .then(r => r.data)
  },

  // List export (feedback #33). Honours the current search/status/date filters;
  // returns a signed download link (Quote_list_[YYYY-MM-DD].[ext]).
  export: (q: QuoteListQuery & { format?: 'xlsx' | 'csv' } = {}) =>
    apiClient
      .get<ApiResponse<ExportLink>>('/quotes/export', { params: q })
      .then(r => r.data),

  // --- Attachments (feedback #31/#32) ---
  listDocuments: (id: string, q: ListQuery = {}) =>
    apiClient
      .get<ApiList<QuoteDocument>>(`/quotes/${id}/documents`, {
        params: withListDefaults(q),
      })
      .then(r => r.data),

  // Upload a new file (multipart) to attach to the quote.
  uploadDocument: (id: string, file: File, docType = 'quote') => {
    const form = new FormData()
    form.append('file', file)
    form.append('doc_type', docType)
    return apiClient
      .post<ApiResponse<QuoteDocument>>(`/quotes/${id}/documents`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(r => r.data)
  },

  // Link an already-stored file (from client documents or the library) to the
  // quote without re-uploading.
  attachExistingDocument: (
    id: string,
    fileId: string,
    docType: 'client' | 'library'
  ) =>
    apiClient
      .post<ApiResponse<QuoteDocument>>(`/quotes/${id}/documents`, {
        file_id: fileId,
        doc_type: docType,
      })
      .then(r => r.data),

  renameDocument: (id: string, documentId: string, name: string) =>
    apiClient
      .patch<
        ApiResponse<QuoteDocument>
      >(`/quotes/${id}/documents/${documentId}/rename`, { name })
      .then(r => r.data),

  removeDocument: (id: string, documentId: string) =>
    apiClient
      .delete<ApiResponse<null>>(`/quotes/${id}/documents/${documentId}`)
      .then(r => r.data),
}

export default quotesApi
